import { Group, SegmentedControl, Select } from '@mantine/core';
import { DatePickerInput } from '@mantine/dates';
import { IconBuilding, IconCalendar } from '@tabler/icons-react';
import { useQuery } from '@tanstack/react-query';
import dayjs from 'dayjs';
import { api, type Page } from '../api/client';
import type { Organization } from '../api/types';
import { isEn, tr } from '../lib/i18n';

export interface RangeFilter {
  from: string;
  to: string;
  organizationId: string;
}

const iso = (d: Date) => dayjs(d).format('YYYY-MM-DD');
const back = (days: number) => iso(new Date(Date.now() - (days - 1) * 86400_000));
export const defaultRange = (): RangeFilter => ({ from: back(30), to: iso(new Date()), organizationId: '' });

const PRESETS = [7, 30, 90, 365];

/** Period presets, a custom date range and a company filter — one compact row. */
export function OverviewFilters({ value, onChange }: { value: RangeFilter; onChange: (v: RangeFilter) => void }) {
  const orgs = useQuery({ queryKey: ['organizations', 'all-for-filter'], queryFn: () => api<Page<Organization>>('/organizations', { query: { limit: 200 } }) });
  const today = iso(new Date());
  const active = value.to === today ? PRESETS.find((d) => back(d) === value.from) : undefined;
  return (
    <Group gap="xs" wrap="wrap" justify="flex-end">
      <SegmentedControl
        size="xs"
        value={active ? String(active) : ''}
        onChange={(v) => onChange({ ...value, from: back(Number(v)), to: today })}
        data={[
          { value: '7', label: tr('7 أيام', '7d') },
          { value: '30', label: tr('30 يوماً', '30d') },
          { value: '90', label: tr('90 يوماً', '90d') },
          { value: '365', label: tr('سنة', '1y') },
        ]}
      />
      <DatePickerInput
        type="range"
        size="xs"
        w={220}
        leftSection={<IconCalendar size={15} stroke={1.7} />}
        valueFormat="D MMM YYYY"
        maxDate={today}
        allowSingleDateInRange
        value={[value.from, value.to]}
        onChange={([from, to]) => { if (from && to) onChange({ ...value, from: String(from).slice(0, 10), to: String(to).slice(0, 10) }); }}
      />
      <Select
        size="xs"
        w={190}
        searchable
        leftSection={<IconBuilding size={15} stroke={1.7} />}
        value={value.organizationId}
        onChange={(v) => onChange({ ...value, organizationId: v ?? '' })}
        nothingFoundMessage={tr('لا نتائج', 'No results')}
        data={[{ value: '', label: tr('كل الشركات', 'All companies') }, ...(orgs.data?.items ?? []).map((o) => ({ value: o.id, label: isEn() && o.name_en ? o.name_en : o.name }))]}
      />
    </Group>
  );
}
